/**
 * Shared vocabulary for the amenity-reservation views (#642): statuses,
 * fee states, rule codes, and — most of all — how a booking's time is
 * read and written.
 *
 * **A booking's time is the amenity's wall-clock time, never an instant
 * in the viewer's zone.** The API hands back `start_local` /
 * `end_local` as naive `YYYY-MM-DDTHH:MM:SS` strings in the HOA's
 * timezone (named on the row as `timezone`), and takes the same form
 * back. Everything here works on those strings by their components; no
 * function passes one through `new Date(string)` or `toLocaleString()`
 * in the browser's zone, which would move "Saturday 2 pm at the
 * clubhouse" for a manager in another state. The one place the browser
 * clock is consulted is `todayIn`, which asks what today's date is *in
 * the HOA's zone*.
 *
 * Pure functions only, so the rules are unit-testable.
 */
import {
  AmenityHoursWindow,
  AmenityReturn,
  AmenitySeasonReturn,
  AmenityUpdate,
  ReservationErrorCode,
  ReservationMoveFeeOutcome,
  ReservationReschedulePreviewReturn,
  ReservationReturn,
  ReservationRuleCode,
  ReservationRuleViolation,
} from '../clients/rust-api-client';
import { apiErrorMessage } from '../utils/apiErrors';

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

export type ReservationStatus =
  | 'pending'
  | 'approved'
  | 'denied'
  | 'cancelled'
  | 'expired'
  | 'completed'
  | 'no_show';

/**
 * Display and group order: what needs a decision first, then what is on
 * the calendar, then history.
 */
export const STATUSES: { value: ReservationStatus; label: string }[] = [
  { value: 'pending', label: 'Pending approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'completed', label: 'Completed' },
  { value: 'no_show', label: 'No-show' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'denied', label: 'Denied' },
  { value: 'expired', label: 'Expired' },
];

/** Holds its slot on the calendar: waiting for a decision, or booked. */
export const ACTIVE_STATUSES: ReservationStatus[] = ['pending', 'approved'];

export const statusLabel = (status: string): string =>
  STATUSES.find((s) => s.value === status)?.label ?? titleCase(status);

export const FEE_STATUS_LABELS: Record<string, string> = {
  none: 'No fee',
  posted: 'Charged to the account',
  reversed: 'Reversed',
  kept: 'Kept (cancelled late)',
  refund_due: 'Refund due — already paid',
  late_fee: 'Late-cancellation fee charged',
  credited: 'Returned — credited to the account',
  refunded: 'Returned — refunded by cheque',
};

export const feeStatusLabel = (status: string): string =>
  FEE_STATUS_LABELS[status] ?? titleCase(status);

/**
 * The fee status as a manager should read it. A charge is only posted on
 * approval, so a pending booking with a fee is `none` on the wire but
 * "Charged on approval" here; "No fee" is kept for a booking that costs
 * nothing.
 */
export const reservationFeeStatusLabel = (r: {
  fee_status: string;
  fee_amount: string;
  status: string;
}): string => {
  if (r.fee_status !== 'none') return feeStatusLabel(r.fee_status);
  if (isZeroMoney(r.fee_amount)) return 'No fee';
  if (r.status === 'pending') return 'Charged on approval';
  return 'Not charged';
};

export const CANCELLED_BY_LABELS: Record<string, string> = {
  household: 'The household',
  management: 'Management',
  system: 'Expired — no one acted in time',
};

/** Who ended a cancelled or expired booking, or null when it is neither. */
export const cancelledByLabel = (kind: string | null | undefined): string | null =>
  kind ? CANCELLED_BY_LABELS[kind] ?? titleCase(kind) : null;

/** The properties-panel "Booked by" line: the name when known, else the realm. */
export const bookedByLabel = (r: {
  booked_by_name?: string | null;
  booked_by_member_id?: string | null;
  booked_by_user_id?: string | null;
}): string => {
  const realm = r.booked_by_member_id ? 'Homeowner' : r.booked_by_user_id ? 'Management' : null;
  if (r.booked_by_name && realm) return `${r.booked_by_name} (${realm.toLowerCase()})`;
  return r.booked_by_name ?? realm ?? '—';
};

/** 'owners_only' → 'Owners Only'. */
export const titleCase = (value: string): string =>
  value
    .split(/[_ ]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

/** A short, stable reference for a reservation — the tail of its public id. */
export const shortRef = (id: string): string =>
  `RSV-${id.replace(/^rsv_/, '').slice(0, 5).toUpperCase()}`;

// ---------------------------------------------------------------------------
// Local time, by its components
// ---------------------------------------------------------------------------

export interface LocalParts {
  year: number;
  month: number; // 1-12
  day: number;
  minute: number; // minutes since local midnight, 0-1440
}

const LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/;
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Components of a naive local datetime string, or null if it is not one. */
export const parseLocal = (value: string | null | undefined): LocalParts | null => {
  if (!value) return null;
  const m = LOCAL_RE.exec(value);
  if (!m) return null;
  return {
    year: Number(m[1]),
    month: Number(m[2]),
    day: Number(m[3]),
    minute: Number(m[4]) * 60 + Number(m[5]),
  };
};

const pad = (n: number): string => String(n).padStart(2, '0');

/** 'YYYY-MM-DD' of a local datetime string ('' when unreadable). */
export const localDay = (value: string | null | undefined): string => {
  const p = parseLocal(value);
  return p ? `${p.year}-${pad(p.month)}-${pad(p.day)}` : '';
};

/** Minutes since local midnight of a local datetime string. */
export const localMinute = (value: string | null | undefined): number | null =>
  parseLocal(value)?.minute ?? null;

/**
 * The wire form for a day and a minute of it: 'YYYY-MM-DDTHH:MM:00'.
 * Minute 1440 is the next day's midnight, as the API expects for a
 * booking that ends at midnight.
 */
export const toLocal = (day: string, minute: number): string => {
  if (minute >= 1440) return `${addDays(day, Math.floor(minute / 1440))}T${minuteToHHMM(minute % 1440)}:00`;
  return `${day}T${minuteToHHMM(minute)}:00`;
};

/** 'HH:MM' for minutes since midnight (1440 → '24:00'). */
export const minuteToHHMM = (minute: number): string =>
  `${pad(Math.floor(minute / 60))}:${pad(minute % 60)}`;

/** Minutes since midnight for 'HH:MM', or null. */
export const hhmmToMinute = (value: string): number | null => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 24 || min > 59 || (h === 24 && min !== 0)) return null;
  return h * 60 + min;
};

/** '2:00 PM', 'noon', 'midnight' for minutes since midnight. */
export const formatMinute = (minute: number): string => {
  const m = ((minute % 1440) + 1440) % 1440;
  if (m === 0) return 'midnight';
  if (m === 720) return 'noon';
  const h = Math.floor(m / 60);
  const mm = m % 60;
  const suffix = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return mm === 0 ? `${h12} ${suffix}` : `${h12}:${pad(mm)} ${suffix}`;
};

/** '2:00 PM' for a local datetime string. */
export const formatLocalTime = (value: string | null | undefined): string => {
  const minute = localMinute(value);
  return minute === null ? '' : formatMinute(minute);
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Day of the week (0 = Sunday) of a 'YYYY-MM-DD' calendar day. */
export const weekdayOf = (day: string): number => {
  const m = DAY_RE.exec(day);
  if (!m) return 0;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay();
};

/** The calendar day `n` days after 'YYYY-MM-DD' (negative goes back). */
export const addDays = (day: string, n: number): string => {
  const m = DAY_RE.exec(day);
  if (!m) return day;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + n));
  return d.toISOString().slice(0, 10);
};

/** 'Sat, Oct 3' (with the year when it is not `thisYear`). */
export const formatDayLabel = (day: string, thisYear?: number): string => {
  const m = DAY_RE.exec(day);
  if (!m) return '';
  const y = Number(m[1]);
  const base = `${WEEKDAYS_SHORT[weekdayOf(day)]}, ${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}`;
  return thisYear !== undefined && y !== thisYear ? `${base}, ${y}` : base;
};

/**
 * 'Sat, Oct 3 · 2 PM – 6 PM' for a booking. One that ends on a later day
 * says so ('… – Sun, Oct 4, 10 AM').
 */
export const formatLocalRange = (
  start: string,
  end: string,
  thisYear?: number
): string => {
  const sDay = localDay(start);
  const eDay = localDay(end);
  const endMinute = localMinute(end);
  const endsAtMidnightNextDay = eDay === addDays(sDay, 1) && endMinute === 0;
  const endText =
    eDay === sDay || endsAtMidnightNextDay
      ? formatLocalTime(end)
      : `${formatDayLabel(eDay, thisYear)}, ${formatLocalTime(end)}`;
  return `${formatDayLabel(sDay, thisYear)} · ${formatLocalTime(start)} – ${endText}`;
};

/** Length of a booking in minutes, by its local components. */
export const durationMinutes = (start: string, end: string): number | null => {
  const s = parseLocal(start);
  const e = parseLocal(end);
  if (!s || !e) return null;
  const dayMs = (p: LocalParts) => Date.UTC(p.year, p.month - 1, p.day);
  return (dayMs(e) - dayMs(s)) / 60000 + e.minute - s.minute;
};

/** '1 hr', '1 hr 30 min', '45 min'. */
export const formatDuration = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
};

/**
 * Today's calendar day in `timeZone` — the HOA's, not the viewer's.
 * `en-CA` formats as YYYY-MM-DD. Falls back to the viewer's day if the
 * zone is unknown to this browser.
 */
export const todayIn = (timeZone: string | null | undefined, now: Date = new Date()): string => {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timeZone || undefined,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  } catch {
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  }
};

/** "Times in America/Chicago" — shown beside every time on these screens. */
export const tzLabel = (timeZone: string | null | undefined): string =>
  timeZone ? `Times in ${timeZone}` : 'Times in the association’s local time';

/**
 * An `<input type="datetime-local">` value ('YYYY-MM-DDTHH:MM') as the
 * API's local form, or null.
 */
export const fromDateTimeInput = (value: string): string | null =>
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value) ? `${value}:00` : null;

/** The API's local form as an `<input type="datetime-local">` value. */
export const toDateTimeInput = (value: string | null | undefined): string =>
  value ? value.slice(0, 16) : '';

/**
 * A moment the API sent as epoch **seconds** (created, decided), in the
 * viewer's own zone — these are instants, not bookings.
 */
export const formatMoment = (seconds: number | null | undefined): string => {
  if (!seconds) return '';
  const d = new Date(seconds * 1000);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
  });
};

// ---------------------------------------------------------------------------
// Opening hours
// ---------------------------------------------------------------------------

/** One weekday's windows, in order. */
export const windowsFor = (
  hours: AmenityHoursWindow[],
  weekday: number
): AmenityHoursWindow[] =>
  hours
    .filter((h) => h.weekday === weekday)
    .sort((a, b) => a.open_minute - b.open_minute);

/** '9 AM – 5 PM, 6 PM – 10 PM' or 'Closed'. */
export const formatWindows = (windows: AmenityHoursWindow[]): string =>
  windows.length === 0
    ? 'Closed'
    : windows
        .map((w) => `${formatMinute(w.open_minute)} – ${formatMinute(w.close_minute)}`)
        .join(', ');

/**
 * The problem with a weekday's windows as entered, or null: each must
 * open before it closes, and they may not overlap.
 */
export const windowProblem = (windows: AmenityHoursWindow[]): string | null => {
  const sorted = [...windows].sort((a, b) => a.open_minute - b.open_minute);
  for (let i = 0; i < sorted.length; i += 1) {
    const w = sorted[i];
    if (w.close_minute <= w.open_minute) return 'Each window must open before it closes.';
    if (i > 0 && w.open_minute < sorted[i - 1].close_minute)
      return 'Windows on the same day may not overlap.';
  }
  return null;
};

// ---------------------------------------------------------------------------
// Rule codes
// ---------------------------------------------------------------------------

/**
 * What each rule code means, phrased for the manager overriding it. The
 * server's `message` carries the specifics ("Needs 48 hours' notice");
 * this is the short name beside the checkbox.
 */
export const RULE_LABELS: Record<ReservationRuleCode, string> = {
  AMENITY_INACTIVE: 'Amenity is inactive',
  AMENITY_NOT_BOOKABLE: 'Amenity is not bookable',
  HOME_NOT_IN_HOA: 'Home is not in this association',
  INVALID_TIME_RANGE: 'Invalid time range',
  IN_PAST: 'In the past',
  SLOT_CONFLICT: 'Slot already taken',
  QUOTE_CHANGED: 'Fee changed since it was quoted',
  DURATION_TOO_SHORT: 'Shorter than the minimum',
  DURATION_TOO_LONG: 'Longer than the maximum',
  OUTSIDE_OPEN_HOURS: 'Outside opening hours',
  CLOSED: 'During a closure',
  TOO_SOON: 'Less notice than required',
  TOO_FAR_AHEAD: 'Further ahead than allowed',
  PARTY_SIZE_EXCEEDED: 'Party too large',
  HOME_DAILY_LIMIT: "Household's daily limit",
  HOME_WEEKLY_LIMIT: "Household's weekly limit",
  HOME_MONTHLY_LIMIT: "Household's monthly limit",
  HOME_ACTIVE_LIMIT: "Household's upcoming-bookings limit",
  ROLE_NOT_ELIGIBLE: 'Owners only',
  NOT_IN_GOOD_STANDING: 'Account not in good standing',
  AGREEMENT_REQUIRED: 'Agreement not accepted',
  AGREEMENT_VERSION_STALE: 'Agreement is not the current version',
  CAPACITY_EXCEEDED: 'Not enough room left',
  NIGHTS_TOO_FEW: 'Fewer nights than the minimum',
  NIGHTS_TOO_MANY: 'More nights than the maximum',
  VEHICLE_REQUIRED: 'Vehicle details missing',
  OUT_OF_SEASON: 'Out of season',
  HOME_WEEKLY_HOURS_LIMIT: "Household's weekly hours",
  HOME_MONTHLY_HOURS_LIMIT: "Household's monthly hours",
  GROUP_DAILY_LIMIT: "Group's daily limit",
  GROUP_WEEKLY_LIMIT: "Group's weekly limit",
  GROUP_MONTHLY_LIMIT: "Group's monthly limit",
  GROUP_ACTIVE_LIMIT: "Group's upcoming-bookings limit",
  HOME_SUSPENDED: 'Household is suspended',
  DOCUMENTS_MISSING: 'Required documents not accepted',
};

export const ruleLabel = (code: string): string =>
  RULE_LABELS[code as ReservationRuleCode] ?? titleCase(code);

/**
 * Whether the override form can go ahead: every violation is overridable,
 * every one is ticked, and there is a reason. The server re-checks all of
 * it — this only keeps the button honest.
 */
export const canSubmitOverrides = (
  violations: ReservationRuleViolation[],
  ticked: string[],
  reason: string
): boolean =>
  violations.length === 0 ||
  (violations.every((v) => v.overridable && ticked.includes(v.code)) &&
    reason.trim().length > 0);

/** A 422 from the reservation endpoints: `{error, violations}`. */
export const ruleViolationsFrom = (err: unknown): ReservationRuleViolation[] | null => {
  const response = (err as { response?: { status?: number; data?: unknown } })?.response;
  if (response?.status !== 422) return null;
  const data = response.data as { violations?: unknown } | undefined;
  return Array.isArray(data?.violations)
    ? (data!.violations as ReservationRuleViolation[])
    : null;
};

/** Shown when an amenity save is refused because someone saved it first. */
export const AMENITY_CHANGED_MESSAGE =
  'Someone else changed this amenity while you were editing. It has been reloaded with their changes — check it and save again.';

const ERROR_CODES = new Set<string>(Object.values(ReservationErrorCode));

/**
 * The machine-readable `code` on a reservation or amenity refusal that is
 * not a rule refusal — `ReservationErrorReturn` (and the 409 conflict
 * list). Branch on this, never on the prose in `error`.
 */
export const reservationErrorCode = (err: unknown): ReservationErrorCode | null => {
  const data = (err as { response?: { data?: unknown } })?.response?.data as { code?: unknown } | undefined;
  return typeof data?.code === 'string' && ERROR_CODES.has(data.code) ? (data.code as ReservationErrorCode) : null;
};

/**
 * What to tell a manager for a code where our words beat the server's;
 * every other code shows the server's own explanation.
 */
const ERROR_WORDING: Partial<Record<ReservationErrorCode, string>> = {
  WRONG_STATUS: 'This reservation has changed since you opened it. Reload to see where it stands.',
  ALREADY_STARTED: 'This booking has already started, so it can no longer be changed that way.',
  ALREADY_OVER: 'This booking is already over.',
  NOT_STARTED: 'This booking has not started yet.',
  DEADLINE_PASSED: 'The deadline for that change has passed.',
  STALE_AMENITY: AMENITY_CHANGED_MESSAGE,
  NAME_TAKEN: 'Another amenity in this association already has that name.',
  AMENITY_ARCHIVED: 'This amenity is archived.',
  SEASON_OVERLAP: 'That season overlaps another season of this amenity.',
  BILLING_CONFLICT: "The home's account changed while this was saving. Try again.",
  GROUP_CHANGED: 'This amenity group changed while you were editing. Reload it and try again.',
  DEPOSIT_NOT_HELD: 'There is no deposit held on this booking to settle.',
  DEPOSIT_PARTLY_PAID:
    "Only part of this deposit has been paid, so it can't be settled here. Settle the home's account first.",
  BOOKING_NOT_OVER: 'The booking has not ended yet — settle the deposit once it is over.',
  WAITLIST_CLOSED: 'That waitlist entry is no longer open.',
  CHECK_IN_CLOSED: 'Check-in is not open for this booking: it opens shortly before the start and closes at the end.',
  NOT_CHECKED_IN: 'The household has not been checked in yet.',
  ALREADY_CHECKED_IN: 'The household checked in, so this booking cannot be marked a no-show.',
  SUSPENSION_LIFTED: 'This suspension has already been lifted.',
};

/** The message for a reservation or amenity refusal, worded by its code where we have words for it. */
export const reservationErrorMessage = (err: unknown, fallback: string): string => {
  const code = reservationErrorCode(err);
  return (code && ERROR_WORDING[code]) || apiErrorMessage(err, fallback);
};

/** What moving the booking does to its fee — `fee_outcome` on a reschedule preview. */
export const MOVE_FEE_OUTCOME_LABELS: Record<ReservationMoveFeeOutcome, string> = {
  unchanged: 'The fee stays the same.',
  requoted: 'The fee is re-quoted; nothing has been charged yet.',
  reposted: 'The current charge is reversed and the new fee charged.',
  returned: 'The paid charge stands, and the difference is returned to the home’s account.',
  kept_paid: 'The paid charge stands unchanged.',
  refused_fee_paid: 'The fee has already been paid, and the new time changes it.',
};

/** "$150.00 → $200.00", or null when the fee does not change. */
export const feeChangeText = (
  p: Pick<ReservationReschedulePreviewReturn, 'fee_change'>,
  money: (v: string) => string
): string | null =>
  p.fee_change.from === p.fee_change.to ? null : `${money(p.fee_change.from)} → ${money(p.fee_change.to)}`;

/** A 409 from closure or archive: `{error, conflicts}` — the bookings in the way. */
export const conflictsFrom = (err: unknown): ReservationReturn[] | null => {
  const response = (err as { response?: { status?: number; data?: unknown } })?.response;
  if (response?.status !== 409) return null;
  const data = response.data as { conflicts?: unknown } | undefined;
  return Array.isArray(data?.conflicts) ? (data!.conflicts as ReservationReturn[]) : null;
};

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

const MONEY_INPUT = /^\d+(\.\d{1,2})?$/;

/** A fee as typed: digits with at most two decimals. Money stays a string. */
export const isValidFee = (value: string): boolean => MONEY_INPUT.test(value.trim());

/** True when a decimal money string is exactly zero ('0', '0.00'). */
export const isZeroMoney = (value: string | null | undefined): boolean =>
  !value || /^[-+]?0*(\.0*)?$/.test(value.trim());

// ---------------------------------------------------------------------------
// Filtering and grouping
// ---------------------------------------------------------------------------

export interface ReservationFilters {
  statuses: ReservationStatus[];
  amenities: string[];
  search: string;
}

/** The manager's default view is the approval queue. */
export const DEFAULT_FILTERS: ReservationFilters = {
  statuses: ['pending'],
  amenities: [],
  search: '',
};

export const applyFilters = (
  rows: ReservationReturn[],
  f: ReservationFilters
): ReservationReturn[] => {
  const q = f.search.trim().toLowerCase();
  return rows.filter((r) => {
    if (f.statuses.length && !f.statuses.includes(r.status as ReservationStatus)) return false;
    if (f.amenities.length && !f.amenities.includes(r.amenity_id)) return false;
    if (!q) return true;
    return [r.amenity_name, r.home_address, r.purpose ?? '', shortRef(r.id), r.id]
      .join(' ')
      .toLowerCase()
      .includes(q);
  });
};

export interface ReservationGroup {
  status: ReservationStatus | 'other';
  label: string;
  reservations: ReservationReturn[];
}

/**
 * By status in STATUSES order. Inside a group, what happens soonest comes
 * first for open work (pending, approved) and latest first for history.
 * An unknown status lands in a trailing "Other" group rather than
 * vanishing.
 */
export const groupByStatus = (rows: ReservationReturn[]): ReservationGroup[] => {
  const soonest = (a: ReservationReturn, b: ReservationReturn) =>
    a.start_local.localeCompare(b.start_local);
  const groups: ReservationGroup[] = STATUSES.map((s) => {
    const inGroup = rows.filter((r) => r.status === s.value);
    inGroup.sort(ACTIVE_STATUSES.includes(s.value) ? soonest : (a, b) => soonest(b, a));
    return { status: s.value, label: s.label, reservations: inGroup };
  });
  const known = new Set<string>(STATUSES.map((s) => s.value));
  const other = rows.filter((r) => !known.has(r.status));
  if (other.length) groups.push({ status: 'other', label: 'Other', reservations: other });
  return groups.filter((g) => g.reservations.length > 0);
};

// ---------------------------------------------------------------------------
// Amenity form
// ---------------------------------------------------------------------------

/**
 * The update body for an amenity as it stands. `PUT /amenities/{id}` is a
 * **full replacement** — an omitted limit becomes unlimited — so every
 * form starts from this and changes only its own fields.
 */
export const amenityToUpdate = (a: AmenityReturn): AmenityUpdate => ({
  // The lost-update guard: the server refuses (409) when the amenity has
  // changed since this copy was loaded, instead of overwriting the other
  // manager's save with stale fields.
  expected_date_updated: a.date_updated ?? null,
  name: a.name,
  description: a.description ?? null,
  location: a.location ?? null,
  rules_text: a.rules_text ?? null,
  booking_mode: a.booking_mode,
  approval_mode: a.approval_mode,
  is_active: a.is_active,
  sort_order: a.sort_order,
  slot_increment_minutes: a.slot_increment_minutes,
  min_duration_minutes: a.min_duration_minutes,
  max_duration_minutes: a.max_duration_minutes,
  buffer_minutes: a.buffer_minutes,
  max_lead_days: a.max_lead_days ?? null,
  min_notice_hours: a.min_notice_hours ?? null,
  cancel_deadline_hours: a.cancel_deadline_hours ?? null,
  max_party_size: a.max_party_size ?? null,
  per_home_daily_limit: a.per_home_daily_limit ?? null,
  per_home_weekly_limit: a.per_home_weekly_limit ?? null,
  per_home_monthly_limit: a.per_home_monthly_limit ?? null,
  per_home_active_limit: a.per_home_active_limit ?? null,
  eligibility: a.eligibility,
  requires_good_standing: a.requires_good_standing,
  requires_agreement: a.requires_agreement,
  fee_type: a.fee_type,
  fee_amount: a.fee_type === 'none' ? null : a.fee_amount ?? null,
  capacity: a.capacity ?? null,
  check_in_minute: a.check_in_minute ?? null,
  check_out_minute: a.check_out_minute ?? null,
  min_nights: a.min_nights ?? null,
  max_nights: a.max_nights ?? null,
  requires_vehicle: a.requires_vehicle,
  late_cancel_policy: a.late_cancel_policy,
  late_cancel_amount: a.late_cancel_policy === 'keep_full' ? null : a.late_cancel_amount ?? null,
  per_home_weekly_hours_limit: a.per_home_weekly_hours_limit ?? null,
  per_home_monthly_hours_limit: a.per_home_monthly_hours_limit ?? null,
});


/** '' → null, '12' → 12; anything else is NaN and fails validation. */
export const optionalInt = (value: string): number | null => {
  const v = value.trim();
  if (v === '') return null;
  return /^\d+$/.test(v) ? Number(v) : NaN;
};

/**
 * A decimal hours limit as typed: '' → null (no limit), '7.5' → '7.5',
 * anything else → undefined (invalid). Kept a string — the API takes
 * decimals as strings.
 */
export const optionalHours = (value: string): string | null | undefined => {
  const v = value.trim();
  if (v === '') return null;
  return /^\d+(\.\d{1,2})?$/.test(v) && Number(v) > 0 ? v : undefined;
};

export const BOOKING_MODE_LABELS: Record<string, string> = {
  exclusive: 'Bookable — one household at a time',
  shared_capacity: 'Shared — several households up to a headcount',
  overnight: 'Overnight stays — by the night',
  info_only: 'Information only — no booking',
};

/** The modes offered in the amenity form, in the order they are offered. */
export const BOOKING_MODES = ['exclusive', 'shared_capacity', 'overnight', 'info_only'] as const;

export const APPROVAL_MODE_LABELS: Record<string, string> = {
  auto: 'Confirmed automatically',
  manual: 'Needs manager approval',
};

export const ELIGIBILITY_LABELS: Record<string, string> = {
  all_residents: 'All residents',
  owners_only: 'Owners only',
};

export const FEE_TYPE_LABELS: Record<string, string> = {
  none: 'Free',
  flat: 'Flat fee per booking',
  hourly: 'Hourly rate',
  per_night: 'Per night',
};

export const LATE_CANCEL_POLICY_LABELS: Record<string, string> = {
  keep_full: 'Keep the whole fee',
  flat: 'Charge a flat late fee instead',
  percent: 'Charge a percentage of the fee instead',
};

/** What cancelling now would do to the fee — `cancel_outcome_now` on a reservation. */
export const CANCEL_OUTCOME_LABELS: Record<string, string> = {
  free: 'Free — nothing was charged',
  reversed: 'The fee would be reversed',
  kept: 'The fee would be kept — past the deadline',
  late_fee: 'A late-cancellation fee would replace the fee',
  returned: 'Already paid — the fee would be returned',
};

export const cancelOutcomeLabel = (outcome: string | null | undefined): string =>
  outcome ? CANCEL_OUTCOME_LABELS[outcome] ?? titleCase(outcome) : '';

// ---------------------------------------------------------------------------
// Stays (overnight amenities)
// ---------------------------------------------------------------------------

/** Nights between a stay's start and end, by their calendar days. */
export const nightsBetween = (start: string, end: string): number | null => {
  const s = localDay(start);
  const e = localDay(end);
  if (!s || !e) return null;
  const [sy, sm, sd] = s.split('-').map(Number);
  const [ey, em, ed] = e.split('-').map(Number);
  return Math.round((Date.UTC(ey, em - 1, ed) - Date.UTC(sy, sm - 1, sd)) / 86_400_000);
};

/**
 * A stay's wire times: check-in on the arrival day, check-out `nights`
 * days later. Times are the amenity's own (`check_in_minute`,
 * `check_out_minute`).
 */
export const stayTimes = (
  arrival: string,
  nights: number,
  checkInMinute: number,
  checkOutMinute: number
): { start_local: string; end_local: string } => ({
  start_local: toLocal(arrival, checkInMinute),
  end_local: toLocal(addDays(arrival, nights), checkOutMinute),
});

/** The nights a stay may last, from the amenity's limits (1–14 when it sets none). */
export const nightOptions = (minNights?: number | null, maxNights?: number | null): number[] => {
  const lo = Math.max(1, minNights ?? 1);
  const hi = Math.max(lo, maxNights ?? 14);
  return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
};

/** '3 nights', '1 night'. */
export const formatNights = (n: number): string => `${n} night${n === 1 ? '' : 's'}`;

/** The vehicle on a booking in one line ('ABC123 (CO) · Blue Subaru'), or null. */
export const vehicleSummary = (r: {
  vehicle_plate?: string | null;
  vehicle_state?: string | null;
  vehicle_description?: string | null;
}): string | null => {
  if (!r.vehicle_plate && !r.vehicle_description) return null;
  const plate = r.vehicle_plate
    ? `${r.vehicle_plate}${r.vehicle_state ? ` (${r.vehicle_state})` : ''}`
    : null;
  return [plate, r.vehicle_description].filter(Boolean).join(' · ');
};

// ---------------------------------------------------------------------------
// Seasons
// ---------------------------------------------------------------------------

/** Whether 'YYYY-MM-DD' falls in a season. Inclusive; an end before its start wraps the year. */
export const inSeason = (
  s: Pick<AmenitySeasonReturn, 'start_month' | 'start_day' | 'end_month' | 'end_day'>,
  day: string
): boolean => {
  const m = DAY_RE.exec(day);
  if (!m) return false;
  const md = Number(m[2]) * 100 + Number(m[3]);
  const start = s.start_month * 100 + s.start_day;
  const end = s.end_month * 100 + s.end_day;
  return start <= end ? start <= md && md <= end : md >= start || md <= end;
};

/** The season `day` is in, if any. */
export const seasonFor = (
  seasons: AmenitySeasonReturn[],
  day: string
): AmenitySeasonReturn | null => seasons.find((s) => inSeason(s, day)) ?? null;

/** 'May 25 – Sep 7'. */
export const formatSeasonRange = (
  s: Pick<AmenitySeasonReturn, 'start_month' | 'start_day' | 'end_month' | 'end_day'>
): string =>
  `${MONTHS[s.start_month - 1] ?? '?'} ${s.start_day} – ${MONTHS[s.end_month - 1] ?? '?'} ${s.end_day}`;

/**
 * What the schedule says about one day — the same resolution the server
 * makes (`rules::day_hours`):
 *
 * - in a season: that season's windows, or the default ones when the
 *   season has none of its own;
 * - outside every season: the default windows; with seasons but no
 *   default windows the amenity is **out of season** that day;
 * - no hours anywhere: open round the clock (`unconfigured`).
 */
export type DayHours =
  | { kind: 'unconfigured' }
  | { kind: 'windows'; windows: AmenityHoursWindow[]; season: AmenitySeasonReturn | null }
  | { kind: 'out_of_season' };

export const dayHours = (
  hours: AmenityHoursWindow[],
  seasons: AmenitySeasonReturn[],
  day: string
): DayHours => {
  const rowsFor = (seasonId: string | null) =>
    hours.filter((w) => (w.season_id ?? null) === seasonId);
  const defaults = rowsFor(null);
  const season = seasonFor(seasons, day);
  let rows: AmenityHoursWindow[];
  if (season) {
    const own = rowsFor(season.id);
    rows = own.length ? own : defaults;
  } else if (seasons.length && !defaults.length) {
    return { kind: 'out_of_season' };
  } else {
    rows = defaults;
  }
  if (!rows.length) return { kind: 'unconfigured' };
  return { kind: 'windows', windows: windowsFor(rows, weekdayOf(day)), season };
};
